import React, {useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from "react-intl";
import {RequestVisitorDto} from "../../../../../modules/vms/dto/RequestVisitorDto";
import {
    Dialog,
    Stack,
    Step, StepLabel,
    Stepper
} from "@mui/material";

import SelfTerminalFormHeader from "../../components/SelfTerminalFormHeader";
import {VisitRequestClassify} from "../../../../../modules/vms/enums/VisitRequestClassify";
import CheckinIdNumberInput from "./CheckinIdNumberInput";
import VisitRequestConfirm from "./VisitRequestConfirm";
import PortraitCapture from './PortraitCapture';
import FinishTipsHeader from "./FinishTips";
import AttachFileCapture from "./AttachFileCapture";
import {RequestVisitorAttachFile} from "../../../../../modules/vms/dto/RequestVisitorAttachFile";
import {VisitorAttachFileClassify} from "../../../../../modules/vms/enums/VisitorFileClassify";
import dayjs from "dayjs";
import {Formats} from "../../../../../constants/Formats";
import VehicleEnvironmental from "./VehicleEnvironmental";
import {RequestVisitorApi} from "../../../../../modules/vms/api/RequestVisitorApi";
import {HttpStatus} from "../../../../../modules/common/enums/HttpStatus";
import LoadingBackdrop from "../../../../../components/LoadingBackdrop";
import AccessCode from './AccessCode';

export type CheckinDialogProps = {
    open: boolean;
    requestVisitor: RequestVisitorDto;
    visitorClassify: string;
    onClose: () => void;
};

const CheckinDialog: React.FC<CheckinDialogProps> = (props) => {

    const intl = useIntl();

    const [innerRequestVisitor, setInnerRequestVisitor] = useState<RequestVisitorDto>(props.requestVisitor);

    const [stepIndex, setStepIndex] = useState(0);

    // 根据不同的访问申请分类，构筑相应的步骤
    const stepTitles = new Array<string>();
    const stepForms = new Array<JSX.Element>();

    stepTitles.push(intl.formatMessage({id: 'VMS_TERMINAL_STEP_VISIT_INFO'}));
    stepTitles.push(intl.formatMessage({id: 'VMS_TERMINAL_STEP_ID_NUMBER'}));

    const visitRequestConfirm = <VisitRequestConfirm onNext={() => {
        setStepIndex(stepIndex + 1)
    }} requestVisitor={innerRequestVisitor}/>

    const checkinIdNumberInput = <CheckinIdNumberInput onPrevious={() => {
        setStepIndex(stepIndex - 1)
    }} onNext={(idNumber) => {
        setInnerRequestVisitor(previousRequestVisitor => ({...previousRequestVisitor, ['idNumber']: idNumber}));
        setStepIndex(stepIndex + 1);
    }} value={innerRequestVisitor.idNumber}/>;

    stepForms.push(visitRequestConfirm);
    stepForms.push(checkinIdNumberInput);

    if (props.visitorClassify === VisitRequestClassify.Enum.Construction) {
        stepTitles.push(intl.formatMessage({id: 'VMS_TERMINAL_STEP_CONSTRUCTION_ORDER'}));
        const constructionCapture = <AttachFileCapture
            title={intl.formatMessage({id: 'VMS_REQUEST_VISITOR_CONSTRUCTION_CAPTURE_TIPS'})} onPrevious={() => {
            setStepIndex(stepIndex - 1)
        }} onNext={(fileId) => {
            // 更新访客对象的附件列表
            const newAttachFiles = props.requestVisitor.attachFilesArray ? [...props.requestVisitor.attachFilesArray] : [];
            const newAttachFile = new RequestVisitorAttachFile();
            newAttachFile.fileId = fileId;
            newAttachFile.classify = VisitorAttachFileClassify.Enum.ConstructionOrder;
            newAttachFile.fileName = dayjs().format(Formats.DATE_TIME_FULL_SIMPLE);
            newAttachFiles.push(newAttachFile);
            setInnerRequestVisitor(previousRequestVisitor => ({
                ...previousRequestVisitor,
                ['attachFilesArray']: newAttachFiles
            }));
            setStepIndex(stepIndex + 1);
        }}/>;
        stepForms.push(constructionCapture);
    } else if (props.requestVisitor.visitRequest?.classify === VisitRequestClassify.Enum.Shipping ||
        props.requestVisitor.visitRequest?.classify === VisitRequestClassify.Enum.SpecialVehicle) {
        // 添加车辆环保信息
        stepTitles.push(intl.formatMessage({id: 'VMS_TERMINAL_STEP_VEHICLE_ENVIRONMENTAL'}));
        const vehicleEnvironmental = <VehicleEnvironmental requestVisitor={innerRequestVisitor} onPrevious={() => {
            setStepIndex(stepIndex - 1)
        }} onNext={(requestVisitor) => {
            setInnerRequestVisitor(requestVisitor);
            setStepIndex(stepIndex + 1);
        }}/>;
        stepForms.push(vehicleEnvironmental);

        // 添加车牌拍照
        stepTitles.push(intl.formatMessage({id: 'VMS_TERMINAL_STEP_SPECIAL_VEHICLE_LICENSE'}));
        const attachFileCapture = <AttachFileCapture
            title={intl.formatMessage({id: 'VMS_REQUEST_VISITOR_SPECIAL_VEHICLE_CAPTURE_TIPS'})} onPrevious={() => {
            setStepIndex(stepIndex - 1)
        }} onNext={(fileId) => {
            // 更新访客对象的附件列表
            const newAttachFiles = props.requestVisitor.attachFilesArray ? [...props.requestVisitor.attachFilesArray] : [];
            const newAttachFile = new RequestVisitorAttachFile();
            newAttachFile.fileId = fileId;
            newAttachFile.classify = VisitorAttachFileClassify.Enum.VehicleLicense;
            newAttachFile.fileName = dayjs().format(Formats.DATE_TIME_FULL_SIMPLE);
            newAttachFiles.push(newAttachFile);
            setInnerRequestVisitor(previousRequestVisitor => ({
                ...previousRequestVisitor,
                ['attachFilesArray']: newAttachFiles
            }));
            setStepIndex(stepIndex + 1);
        }}/>;
        stepForms.push(attachFileCapture);
    }
    // stepTitles.push(intl.formatMessage({id: 'VMS_TERMINAL_STEP_PORTRAIT'}));
    // stepTitles.push(intl.formatMessage({id: 'VMS_TERMINAL_STEP_ACCESS_CODE'}));
    stepTitles.push(intl.formatMessage({id: 'VMS_TERMINAL_STEP_FINISH'}));

    // const portraitCapture = <PortraitCapture onPrevious={() => {
    //     setStepIndex(stepIndex - 1)
    // }} onNext={(fileId) => {
    //     setInnerRequestVisitor(previousRequestVisitor => ({...previousRequestVisitor, ['portraitFileId']: fileId}));
    //     setStepIndex(stepIndex + 1);
    // }}/>;
    // stepForms.push(portraitCapture);

    // const accessCode = <AccessCode onPrevious={() => {
    //     setStepIndex(stepIndex - 1)
    // }} onNext={() => {
    //     setStepIndex(stepIndex + 1);
    // }} requestVisitor={innerRequestVisitor}/>;
    // stepForms.push(accessCode);

    const finishTipsHeader = <FinishTipsHeader onBackHome={() => props.onClose()}
                                               requestVisitor={innerRequestVisitor}/>
    stepForms.push(finishTipsHeader);

    useEffect(() => {
        if (props.open) {
            setInnerRequestVisitor({...props.requestVisitor});
            setStepIndex(0);
        }
    }, [props.open]);

    const handleCancel = () => {
        props.onClose();
    }

    const handleSubmit = () => {
        props.onClose();
    }

    return (
        <Dialog open={props.open} fullScreen>
            <div className="self-terminal-page">
                <SelfTerminalFormHeader onBackHome={handleCancel}/>
                <div className="self-terminal-page-step-content">
                    <Stack spacing={2}>
                        <Stepper activeStep={stepIndex} alternativeLabel>
                            {stepTitles.map((item, index,) => (
                                <Step key={index}>
                                    <StepLabel>{item}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        <div className="self-terminal-form-panel">
                            {stepForms[stepIndex]}
                        </div>
                    </Stack>
                </div>
            </div>
        </Dialog>
    );
};

export default CheckinDialog;
